"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useCheckout } from "@/hooks/useCheckout";
import { PRICING_PLANS, type PricingPlanId } from "@/lib/billing/plans";
import { FEATURE_GATES, type FeatureKey } from "@/lib/billing/featureGates";
import { CheckIcon, LockIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pluralDays(n: number): string {
  return `${n} day${n === 1 ? "" : "s"}`;
}

function trialCountdownCopy(trialDaysLeft: number | null): string | null {
  if (trialDaysLeft === null) return null;
  if (trialDaysLeft === 0) return "Your free trial ends today.";
  if (trialDaysLeft === 1) return "Your free trial ends tomorrow.";
  return `Your free trial ends in ${pluralDays(trialDaysLeft)}.`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeatureUpgradePromptProps {
  /** The feature key whose requirements are not met */
  featureKey: FeatureKey;
  /**
   * Days remaining on free trial — passed from useFeatureGate.
   * Renders a trial countdown note in the prompt header when provided.
   * null when the user is not on a trial.
   */
  trialDaysLeft?: number | null;
  /**
   * When true, renders as a full-screen modal overlay with a backdrop.
   * When false (default), renders as an inline centered card.
   */
  asModal?: boolean;
  /**
   * Only used in modal mode. When false the modal is not rendered.
   * Allows controlling visibility from the parent without unmounting.
   */
  open?: boolean;
  /**
   * Called when the user dismisses the modal (backdrop click, "Dismiss" button).
   * Required when asModal=true to allow the parent to close the modal.
   */
  onClose?: () => void;
}

// ─── Plan card ────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: (typeof PRICING_PLANS)[number];
  isRecommended: boolean;
}

function PlanCard({ plan, isRecommended }: PlanCardProps) {
  const { startCheckout, loadingPlan, error } = useCheckout();

  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border p-4",
        isRecommended
          ? "border-primary/50 bg-primary/[0.04] ring-1 ring-primary/20"
          : "border-border"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-foreground">{plan.name}</p>
          <p className="mt-0.5 text-xs text-muted">{plan.description}</p>
        </div>
        {plan.badge && (
          <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
            {plan.badge}
          </span>
        )}
      </div>

      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-2xl font-bold text-foreground">
          ${plan.priceMonthly.toFixed(2)}
        </span>
        <span className="text-xs text-muted">/mo</span>
      </div>

      <ul className="my-3 flex-1 space-y-1.5">
        {plan.features.slice(0, 3).map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-muted">
            <CheckIcon className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
            {f}
          </li>
        ))}
      </ul>

      {error && (
        <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>
      )}

      <Button
        className="w-full"
        size="sm"
        variant={isRecommended ? "default" : "outline"}
        disabled={loadingPlan !== null}
        onClick={() =>
          startCheckout(plan.id as PricingPlanId, {
            successRedirect: "/dashboard?subscription=success",
          })
        }
      >
        {loadingPlan === plan.id ? "Loading..." : "Subscribe Now"}
      </Button>
    </div>
  );
}

// ─── Inner prompt card (shared between modal and inline) ──────────────────────

interface PromptCardProps {
  featureKey: FeatureKey;
  trialDaysLeft: number | null;
  onClose?: () => void;
}

function PromptCard({ featureKey, trialDaysLeft, onClose }: PromptCardProps) {
  const gate = FEATURE_GATES[featureKey];
  const countdownCopy = trialCountdownCopy(trialDaysLeft);
  const isUrgent = trialDaysLeft !== null && trialDaysLeft <= 1;
  const isWarning = trialDaysLeft !== null && trialDaysLeft > 1 && trialDaysLeft <= 3;

  // Filter PRICING_PLANS to the eligible set for this feature gate
  const eligiblePlans = PRICING_PLANS.filter((plan) =>
    gate.eligiblePlanIds.includes(plan.id as "starter" | "plus" | "pro")
  );

  return (
    <div className="w-full max-w-xl rounded-2xl border border-border bg-background shadow-2xl">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="border-b border-border px-6 py-5">
        {/* Trial urgency badge */}
        {isUrgent && (
          <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Trial Ending Soon
          </div>
        )}
        {isWarning && (
          <div className="mb-2.5 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Free Trial Active
          </div>
        )}

        {/* Lock icon */}
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-foreground/5">
          <LockIcon className="h-5 w-5 text-muted" />
        </div>

        <h2
          id="feature-upgrade-prompt-title"
          className="text-lg font-bold text-foreground"
        >
          Upgrade to unlock {gate.label}
        </h2>
        <p className="mt-1 text-sm text-muted">{gate.description}</p>

        {/* Trial countdown note */}
        {countdownCopy && (
          <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
            {countdownCopy} Subscribe now to keep access.
          </p>
        )}
      </div>

      {/* ── Plan cards ──────────────────────────────────────────────────── */}
      <div className="p-6">
        <div
          className={cn(
            "grid gap-3",
            eligiblePlans.length === 1
              ? "grid-cols-1"
              : "grid-cols-1 sm:grid-cols-2"
          )}
        >
          {eligiblePlans.map((plan) => {
            const isRecommended =
              plan.badge === "Most Popular" ||
              (gate.requiredTier === "pro" && plan.id === "pro");
            return (
              <PlanCard key={plan.id} plan={plan} isRecommended={isRecommended} />
            );
          })}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-muted">
            Cancel anytime &bull;{" "}
            <Link
              to="/pricing"
              className="underline underline-offset-2 hover:text-foreground"
              onClick={onClose}
            >
              View all plans
            </Link>
          </p>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-muted transition-colors hover:text-foreground"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── FeatureUpgradePrompt ─────────────────────────────────────────────────────

/**
 * FeatureUpgradePrompt
 *
 * In-component upgrade UI shown when `useFeatureGate(featureKey).allowed`
 * returns false. Displays the feature name, description, trial countdown
 * (when the user is trialing), and plan cards with direct Polar.sh checkout
 * CTAs.
 *
 * Renders in two modes:
 *  - Inline (default): A centered card replacing the gated content.
 *  - Modal: A full-screen overlay with backdrop (set asModal=true).
 *
 * @example
 * ```tsx
 * // Inline — replaces the gated content
 * const { allowed, trialDaysLeft } = useFeatureGate("advanced_stats");
 * if (!allowed) {
 *   return <FeatureUpgradePrompt featureKey="advanced_stats" trialDaysLeft={trialDaysLeft} />;
 * }
 *
 * // Modal — controlled open state from parent
 * const [open, setOpen] = useState(false);
 * <FeatureUpgradePrompt featureKey="premium_server_types" asModal open={open} onClose={() => setOpen(false)} />
 * ```
 */
export function FeatureUpgradePrompt({
  featureKey,
  trialDaysLeft = null,
  asModal = false,
  open = true,
  onClose,
}: FeatureUpgradePromptProps) {
  // Modal mode — respect open flag, render nothing when closed
  if (asModal) {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden="true"
        />
        {/* Dialog */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="feature-upgrade-prompt-title"
          className="relative z-50 w-full max-w-xl"
        >
          <PromptCard
            featureKey={featureKey}
            trialDaysLeft={trialDaysLeft}
            onClose={onClose}
          />
        </div>
      </div>
    );
  }

  // Inline mode — centered card replacing gated content
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16">
      <PromptCard
        featureKey={featureKey}
        trialDaysLeft={trialDaysLeft}
        onClose={onClose}
      />
    </div>
  );
}

// ─── FeatureUpgradeBanner ─────────────────────────────────────────────────────

interface FeatureUpgradeBannerProps {
  /** The feature key being soft-gated */
  featureKey: FeatureKey;
  /**
   * Days remaining on free trial from useFeatureGate.
   * Shown in banner copy when provided.
   */
  trialDaysLeft?: number | null;
  /** Optional additional Tailwind classes on the banner root element */
  className?: string;
}

/**
 * FeatureUpgradeBanner
 *
 * Compact single-row soft-gate banner. Shown at the top of a section to
 * indicate an upgrade is required without blocking the full page.
 * Clicking "Upgrade Now" opens a FeatureUpgradePrompt modal overlay.
 *
 * @example
 * ```tsx
 * const { allowed, trialDaysLeft } = useFeatureGate("premium_server_types");
 * return (
 *   <div>
 *     {!allowed && (
 *       <FeatureUpgradeBanner featureKey="premium_server_types" trialDaysLeft={trialDaysLeft} />
 *     )}
 *     <ServerTypeSelector disabled={!allowed} />
 *   </div>
 * );
 * ```
 */
export function FeatureUpgradeBanner({
  featureKey,
  trialDaysLeft = null,
  className,
}: FeatureUpgradeBannerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const gate = FEATURE_GATES[featureKey];

  const trialNote =
    trialDaysLeft !== null
      ? trialDaysLeft <= 1
        ? " Your trial ends today."
        : ` Your trial ends in ${pluralDays(trialDaysLeft)}.`
      : "";

  return (
    <>
      <div
        role="alert"
        className={cn(
          "rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
          className
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>
            <strong>{gate.label}</strong> requires an upgraded plan.
            {trialNote}
          </span>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="shrink-0 rounded-md bg-amber-700 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-800 dark:bg-amber-600 dark:hover:bg-amber-700"
          >
            Upgrade Now
          </button>
        </div>
      </div>

      <FeatureUpgradePrompt
        featureKey={featureKey}
        trialDaysLeft={trialDaysLeft}
        asModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
